/**
 * Fahryo Analytics — Conversion Funnel & Activity Tracking (CONV-01 + CONV-02)
 *
 * Two-layer tracking:
 * 1. **Sentry** (real-time): captureMessage with level=info for instant observability
 * 2. **Server** (persistent): POST /api/activity/track → user_activity_log table
 *
 * Funnel stages: registration → first_question → exam → paywall → purchase
 * Activity events: login, session_complete, referral_shared
 *
 * All tracking is fire-and-forget — failures are silently swallowed
 * to never block the user experience.
 */
import * as Sentry from '@sentry/react';
import { supabase } from './supabaseClient';
import { trackGA4 } from './analytics-ga4';

const IS_PROD = typeof window !== 'undefined' && window.location.hostname !== 'localhost';

// ── Sentry-only events (UI telemetry, no DB persistence needed) ──
type SentryOnlyEvent =
  | 'paywall_plan_selected'
  | 'placement_started'
  | 'placement_completed'
  | 'journey_step_completed'
  | 'ai_rating_submitted'
  | 'preview_chapter_started'
  | 'preview_limit_reached';

// ── Funnel events (persisted to DB via API) ──
type FunnelEvent =
  | 'registration'
  | 'login'
  | 'first_question'
  | 'session_complete'
  | 'exam_started'
  | 'exam_complete'
  | 'exam_passed'
  | 'paywall_shown'
  | 'paywall_dismissed'
  | 'paywall_cta_clicked'
  | 'purchase'
  | 'referral_shared';

type TrackableEvent = FunnelEvent | SentryOnlyEvent;

interface EventData {
  [key: string]: string | number | boolean | undefined;
}

// Events that should be persisted to user_activity_log via server API
const PERSISTENT_EVENTS: ReadonlySet<string> = new Set<string>([
  'registration',
  'login',
  'first_question',
  'session_complete',
  'exam_started',
  'exam_complete',
  'exam_passed',
  'paywall_shown',
  'paywall_dismissed',
  'paywall_cta_clicked',
  'referral_shared',
]);

/**
 * Track a conversion funnel or activity event.
 *
 * - Always logs to Sentry (real-time monitoring)
 * - For funnel events, also persists to user_activity_log via server API
 *
 * @param event - The event name
 * @param data - Optional structured metadata
 */
export function trackEvent(event: TrackableEvent, data?: EventData): void {
  // Layer 1: Sentry breadcrumb (attaches to real errors as context, no issue
  // grouping). Previously captureMessage at info-level created one Sentry issue
  // per event name, polluting the dashboard with non-error analytics noise.
  if (IS_PROD) {
    Sentry.addBreadcrumb({
      category: 'analytics',
      type: 'info',
      level: 'info',
      message: event,
      data: { event, category: event.split('_')[0], ...(data || {}) },
    });
  } else {
    console.debug(`[Analytics] ${event}`, data || '');
  }

  // Layer 2: Server persistence (funnel events only)
  if (PERSISTENT_EVENTS.has(event)) {
    persistToServer(event as FunnelEvent, data);
  }

  // Layer 3: GA4 (no-op when consent denied or measurement ID unset).
  // GA4 expects flat string/number/boolean params — strip undefineds for safety.
  const ga4Params: Record<string, string | number | boolean> = {};
  if (data) {
    for (const [k, v] of Object.entries(data)) {
      if (v !== undefined) ga4Params[k] = v;
    }
  }
  trackGA4(event, ga4Params);
}

/**
 * Fire-and-forget server-side persistence.
 * Uses the authenticated session token from Supabase.
 */
async function persistToServer(action: FunnelEvent, metadata?: EventData): Promise<void> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) return; // Not logged in yet (e.g. registration event before session)

    const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:3000' : '';
    // Use AbortController to prevent hanging requests on iOS
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    await fetch(`${apiBase}/api/activity/track`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, metadata }),
      signal: controller.signal,
    });
    clearTimeout(timeout);
  } catch {
    // Silent failure — tracking must never block the user
  }
}
